import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Sunrise, Inbox, FileCheck, ClipboardCheck, AlertTriangle, PackagePlus, Store, ChevronRight, Check, ArrowRight, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { Card, Badge, Button, PageLoader } from "../lib/ui";
import { useT } from "../lib/i18n";
import { setGuidedReturn } from "../components/GuidedReturnBanner";

const ICON = { receive: Inbox, verify: FileCheck, check: ClipboardCheck, variance: AlertTriangle, store: PackagePlus, ready: Store };
const MO_ROUTES = ["/receiving", "/my-inventory"];

export default function MorningOps() {
  const { t } = useT();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/workspace/morning-ops"); setData(data); setDenied(false); }
    catch (e) { if (e.response?.status === 400 || e.response?.status === 403) setDenied(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const go = (route) => { if (route !== "/") setGuidedReturn("/morning-ops", "Morning Operations", MO_ROUTES); navigate(route); };

  if (denied) return <Card className="m-4 p-6 text-center text-sm text-ink-mute" data-testid="morning-ops-denied">{t("Morning Ops is for branch accounts. Select a branch from the dashboard.")}</Card>;
  if (!data) return <PageLoader />;
  const pct = data.total_steps ? Math.round((data.completed_steps / data.total_steps) * 100) : 0;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2" data-testid="morning-ops">
      <div className="mb-5 rounded-3xl bg-gradient-to-br from-amber-400 via-orange-400 to-rose-400 p-6 text-white shadow-card">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20"><Sunrise size={24} /></span>
          <div>
            <p className="font-heading text-2xl font-extrabold leading-tight">{t("Morning Operations")}</p>
            <p className="text-sm text-white/80">{data.branch_name} · {data.cycle_date}</p>
          </div>
          <div className="ml-auto text-right">
            <Badge tone={data.incoming_count > 0 ? "warn" : "success"} data-testid="mo-incoming">{data.incoming_count} {t("incoming")}</Badge>
          </div>
        </div>
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-xs font-bold text-white/90">
            <span>{t("Progress")}</span><span data-testid="mo-progress">{data.completed_steps}/{data.total_steps}</span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/25" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full bg-white transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {data.ready_for_business && (
        <div className="mb-5 flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="mo-ready">
          <CheckCircle2 size={22} className="text-success-text" />
          <p className="text-sm text-ink-soft"><b className="text-ink">{t("Restaurant ready for business!")}</b> {t("All deliveries received and stored.")}</p>
        </div>
      )}

      {/* Incoming deliveries to receive */}
      {data.deliveries.length > 0 && (
        <Card className="mb-5 overflow-hidden p-0" data-testid="mo-deliveries">
          <div className="flex items-center gap-2 border-b border-line px-5 py-4">
            <Inbox size={18} className="text-primary" />
            <h3 className="font-heading text-base font-extrabold">{t("Incoming Deliveries")}</h3>
            <Badge tone="warn" className="ml-auto">{data.deliveries.length}</Badge>
          </div>
          <div className="divide-y divide-line">
            {data.deliveries.map((d) => (
              <div key={d.order_id} className="flex items-center gap-3 px-5 py-3" data-testid={`mo-delivery-${d.order_no}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-ink">{d.order_no}</p>
                  <p className="text-xs text-ink-mute">{d.item_count} {t("items")}</p>
                </div>
                <Button size="sm" onClick={() => go("/receiving")} data-testid={`mo-receive-${d.order_no}`}>{t("Receive & Inspect")} <ChevronRight size={15} /></Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Guided checklist */}
      <div className="space-y-3">
        {data.steps.map((s, i) => {
          const Icon = ICON[s.key] || Check;
          return (
            <Card key={s.key} className={`flex items-center gap-4 p-4 ${s.done ? "border-emerald-200 bg-emerald-50/40" : ""}`} data-testid={`mo-step-${s.key}`}>
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${s.done ? "bg-success text-white" : "bg-primary-light text-primary"}`}>
                {s.done ? <Check size={22} strokeWidth={3} /> : <Icon size={20} />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-bold text-stone-400">{i + 1}</span>
                  <p className="font-heading text-base font-extrabold text-ink">{t(s.label)}</p>
                  {s.key === "variance" && s.count > 0 && <Badge tone="danger">{s.count} {t("open")}</Badge>}
                </div>
                <p className="text-xs text-ink-mute">{s.done ? t("Completed") : (s.detail || t("Pending"))}</p>
              </div>
              <Button variant={s.done ? "outline" : "primary"} size="sm" onClick={() => go(s.route)} data-testid={`mo-open-${s.key}`}>
                {s.done ? t("Review") : t("Open")} <ChevronRight size={15} />
              </Button>
            </Card>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-center gap-2 rounded-2xl border border-dashed border-line bg-app py-3 text-sm text-ink-soft" data-testid="mo-handoff">
        {t("Next")}: <b className="text-ink">POS</b> <ArrowRight size={15} className="text-primary" />
      </div>
    </div>
  );
}
